import type { ComponentProps, ReactElement } from "react";
import { useCallback, memo } from "react";

import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import {
    type TableColumnsSelection,
    type TriggerProps,
    ColumnsSelectionButtonContainer,
    ColumnsSelectionContainer,
} from "app/pages/sales-intelligence/common-components/column-picker";
import { selectActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import type { SuggestionsApplyFunc } from "app/pages/sales-intelligence/modules/leads-search/hooks/useFilterColumnSuggestions";
import useFilterColumnSuggestions from "app/pages/sales-intelligence/modules/leads-search/hooks/useFilterColumnSuggestions";
import { ColumnSuggestionsView } from "app/pages/sales-intelligence/sub-modules/column-suggestions/components/ColumnSuggestionsView";

import { useListsColumnsConfig } from "../../../hooks/useListsColumnsConfig";
import { useListsColumnSelectionOptions } from "../../../hooks/useListsColumnSelectionOptions";

type Props = {
    isDisabled: boolean;
    onSuggestionsApply: SuggestionsApplyFunc;
} & Pick<ComponentProps<typeof TableColumnsSelection>, "renderSelectionModal">;

const TableListsColumnsSelectionContainer = ({
    isDisabled,
    onSuggestionsApply,
    renderSelectionModal,
}: Props): ReactElement => {
    const activeLeadsType = useSelector(selectActiveLeadsType);
    const columnsConfig = useListsColumnsConfig(activeLeadsType);

    const { selectedColumnIds, availableColumns } = useListsColumnSelectionOptions(columnsConfig, activeLeadsType);

    const [filterColumnSuggestions, handleSuggestionsApply] = useFilterColumnSuggestions(
        onSuggestionsApply,
        selectedColumnIds,
        availableColumns,
        columnsConfig,
    );

    const renderSelectionTrigger = useCallback(
        ({ onClick }: TriggerProps): ReactElement => {
            if (filterColumnSuggestions.length === 0) {
                return (
                    <ColumnsSelectionButtonContainer
                        isDisabled={isDisabled}
                        suggestionsCount={filterColumnSuggestions.length}
                        tooltipContent={translate("si.columns-selection.tooltip.change-columns")}
                        onClick={onClick}
                    />
                );
            }

            return (
                <ColumnSuggestionsView
                    columnSuggestions={filterColumnSuggestions}
                    isTriggerDisabled={isDisabled}
                    renderTrigger={({ isViewOpen, onViewToggle }) => (
                        <ColumnsSelectionButtonContainer
                            isDisabled={isDisabled}
                            suggestionsCount={filterColumnSuggestions.length}
                            tooltipContent={translate("si.columns-selection.tooltip.view-suggestions")}
                            withTooltip={!isViewOpen}
                            onClick={onViewToggle}
                        />
                    )}
                    onApplyClick={handleSuggestionsApply}
                    onCancelClick={onClick}
                />
            );
        },
        [filterColumnSuggestions, handleSuggestionsApply, isDisabled],
    );

    return (
        <ColumnsSelectionContainer
            renderSelectionTrigger={renderSelectionTrigger}
            renderSelectionModal={renderSelectionModal}
        />
    );
};

export default memo(TableListsColumnsSelectionContainer);
